<template>
  <div
    class="staff-document-row-wrapper"
    data-cy="staff_document_row"
  >
    <div class="staff-document-row">
      <div class="staff-document-row__icon">
        <svg
          aria-hidden="true"
          fill="none"
          height="20"
          viewBox="0 0 20 20"
          width="20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.5 2H5.75A1.75 1.75 0 0 0 4 3.75v12.5c0 .966.784 1.75 1.75 1.75h8.5A1.75 1.75 0 0 0 16 16.25V6.5L11.5 2Z"
            stroke="currentColor"
            stroke-linejoin="round"
            stroke-width="1.5"
          />
          <path
            d="M11.5 2v4.5H16"
            stroke="currentColor"
            stroke-linejoin="round"
            stroke-width="1.5"
          />
        </svg>
      </div>

      <div class="staff-document-row__content">
        <div class="staff-document-row__title">
          {{ row.category }}
        </div>

        <div
          v-if="hasMetadata"
          class="staff-document-row__meta"
        >
          <a
            v-if="row.fileName && row.attachment?.url"
            :href="row.attachment.url"
            class="staff-document-row__meta-item staff-document-row__meta-item--link"
            target="_blank"
          >
            {{ row.fileName }}
          </a>
          <span
            v-else-if="row.fileName"
            class="staff-document-row__meta-item"
          >
            {{ row.fileName }}
          </span>

          <span
            v-if="row.uploadedAt"
            class="staff-document-row__meta-item"
          >
            Uploaded {{ formatShortDate(row.uploadedAt) }}
          </span>

          <button
            v-if="row.expiresAt"
            @click="emit('edit-expiry', row)"
            class="staff-document-row__meta-item staff-document-row__meta-item--expires staff-document-row__meta-item--clickable"
            data-cy="staff_document_expires"
            type="button"
          >
            <svg
              aria-hidden="true"
              class="staff-document-row__calendar"
              fill="none"
              height="14"
              viewBox="0 0 14 14"
              width="14"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                height="10"
                rx="1.5"
                stroke="currentColor"
                stroke-width="1.2"
                width="11"
                x="1.5"
                y="2.5"
              />
              <path
                d="M1.5 5.5h11"
                stroke="currentColor"
                stroke-width="1.2"
              />
              <path
                d="M4.5 1.5v2M9.5 1.5v2"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-width="1.2"
              />
            </svg>
            Expires {{ formatShortDate(row.expiresAt) }}
          </button>
        </div>
      </div>

      <StaffDocumentStatusBadge
        @click="handleStatusClick"
        :clickable="isStatusClickable"
        :status="row.status"
      />
    </div>

    <div
      v-if="row.emptyLabel && !row.fileName"
      class="staff-document-row__empty"
    >
      {{ row.emptyLabel }}
    </div>
  </div>
</template>

<script setup>
import { DISPLAY_STATUSES } from '@/shared/utils/staffDocumentUtils';
import StaffDocumentStatusBadge from '@/shared/components/provider/staff-documents/StaffDocumentStatusBadge.vue';
import useDateFormatting from '@/shared/composables/useDateFormatting';

const props = defineProps({
  canReviewDocuments: {
    type: Boolean,
    default: true,
  },
  row: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['edit-expiry', 'review-status']);

const { formatShortDate } = useDateFormatting();

const hasMetadata = computed(
  () => props.row.fileName || props.row.uploadedAt || props.row.expiresAt,
);

const isStatusClickable = computed(() => {
  if (!props.canReviewDocuments) return false;

  return (
    props.row.status === DISPLAY_STATUSES.PENDING ||
    props.row.status === DISPLAY_STATUSES.ACTIVE ||
    props.row.status === DISPLAY_STATUSES.DENIED
  );
});

function handleStatusClick() {
  if (!isStatusClickable.value) return;

  emit('review-status', props.row);
}
</script>

<style scoped>
.staff-document-row-wrapper {
  padding: 18px 0;
}

.staff-document-row {
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: auto 1fr auto;
}

.staff-document-row__empty {
  color: #6b7280;
  font-size: 14px;
  margin-left: 56px;
  margin-top: 4px;
}

.staff-document-row__icon {
  align-items: center;
  background: #f1f3f6;
  border-radius: 10px;
  color: #6b7280;
  display: flex;
  height: 40px;
  justify-content: center;
  width: 40px;
}

.staff-document-row__title {
  color: #111827;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
}

.staff-document-row__meta {
  color: #6b7280;
  display: flex;
  flex-wrap: wrap;
  font-size: 14px;
  gap: 8px 16px;
  line-height: 1.4;
  margin-top: 4px;
}

.staff-document-row__meta-item {
  align-items: center;
  display: inline-flex;
  gap: 6px;
}

.staff-document-row__meta-item--link {
  color: inherit;
  text-decoration: underline;
}

.staff-document-row__meta-item--clickable {
  background: transparent;
  border: 0;
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-decoration: underline;
  text-decoration-color: transparent;
  transition:
    color 0.15s ease,
    text-decoration-color 0.15s ease;
}

.staff-document-row__meta-item--clickable:hover,
.staff-document-row__meta-item--clickable:focus-visible {
  color: #111827;
  text-decoration-color: currentColor;
}

.staff-document-row__calendar {
  flex: none;
}
</style>
